import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { History, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * Historial de asignaciones del claim (quién asignó a quién y cuándo), en un modal centrado.
 *
 * Lee el MISMO endpoint append-only que usaba el viejo ClaimAssignPanel (quitado por duplicar
 * la tarjeta editable): GET /api/claims/:id/assignment-history. Cada fila trae el kind
 * ('account_manager' | 'team'), el valor nuevo ya resuelto a nombre (value_label), la fecha y
 * el autor (by = nombre; null = lo hizo el sistema, p. ej. el round-robin desde 89e59ab).
 *
 * Clases .cdw-mmodal-* (regla de oro móvil: z-index 4000) + .ash-* que les dan el mismo
 * aspecto fuera del media query móvil (ver bloque ASH al final de styles.css).
 */
type HistoryRow = {
  id: string;
  kind: string;                 // 'account_manager' | 'team'
  dept_key: string | null;      // solo para kind='team': claims | supplements | payments
  value_id: string | null;
  value_label: string | null;   // nombre del AM o del team; null = se quitó la asignación
  created_at: string;
  by: string | null;            // nombre del actor; null = sistema (round-robin / routing)
};

export function AssignmentHistoryModal({ claimId, onClose }: { claimId: string; onClose: () => void }) {
  const { t } = useI18n();
  // null = cargando; [] = cargó vacío. El endpoint ya viene ordenado del más reciente al más viejo.
  const [rows, setRows] = useState<HistoryRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    api.get<{ history: HistoryRow[] }>(`/api/claims/${claimId}/assignment-history`)
      .then((r) => { if (alive) setRows(r.history || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [claimId]);

  // Escape cierra (mismo gesto que el click en el backdrop).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fmt = (iso: string) => new Date(iso).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric' });
  // Depto del team con su label i18n existente (dept.claims / dept.payments / dept.supplements);
  // si llega un depto fuera de esos tres, se muestra crudo en vez de inventar una clave.
  const deptLabel = (k: string | null) => {
    if (!k) return null;
    return ['claims', 'payments', 'supplements'].includes(k) ? t(`dept.${k}`) : k;
  };

  return (
    <div className="cdw-mmodal-backdrop ash-modal-backdrop" onClick={onClose}>
      <div className="cdw-mmodal ash-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cdw-mmodal-head ash-modal-head">
          <b><History size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ash.title')}</b>
          <button type="button" className="btn ghost sm" onClick={onClose}>
            <X size={15} strokeWidth={1.75} />
          </button>
        </div>
        {/* Body SIN .cdw-mmodal-body: esa clase infla los .badge en móvil (padding 10px,
            centrados) y acá los badges son chips chicos dentro de cada fila del historial. */}
        <div className="ash-rows">
          {rows === null && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('common.loadingDots')}</div>}
          {rows !== null && rows.length === 0 && (
            <div className="muted" style={{ fontSize: 'var(--fs-sm)', padding: '8px 0' }}>{t('ash.empty')}</div>
          )}
          {rows !== null && rows.map((h) => (
            <div key={h.id} className="ash-row">
              <span className="ash-when muted" title={new Date(h.created_at).toLocaleString('en-US')}>{fmt(h.created_at)}</span>
              <span className="ash-what">
                {h.kind === 'account_manager'
                  ? <span className="badge" style={{ background: 'var(--info-soft)', color: 'var(--info)', fontSize: 'var(--fs-2xs)' }}>{t('ash.am')}</span>
                  : <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ash.team')}{h.dept_key ? ` · ${deptLabel(h.dept_key)}` : ''}</span>}
                <b className="ash-value">{h.value_label || `— ${t('ash.none')}`}</b>
                <span className="muted ash-by">· {h.by || t('ash.system')}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
